import {
  BASEMAP_COARSEST_LEVEL,
  BASEMAP_TILE_PIXELS,
  type BasemapFeatureSetRules,
  BasemapFeatureTile,
  type BasemapGeometry,
  type BasemapPaintStep,
  decodeFeatureTile,
  FACE_UNITS,
  type LeafTree,
  NO_CATEGORY,
  placeContaining,
  placeKey,
  TILE_AREA,
  type TilePlace,
} from "@pax/basemap";

/**
 * Feature sets drawn by the raster renderer. Each part of a set a paint step can cover (a line
 * set's lines, a polygon set's fills or outlines) becomes a category raster of its own, holding one
 * value wherever the part covers a pixel, and a paint step that covers the part colors those pixels
 * as it would a raster's. Its tiles are drawn from the set's clipped tiles: the stored tile where
 * the set's detail goes that deep, and past it, the finest stored tile drawn again into the finer
 * place, so lines stay sharp however far the view zooms.
 */

export type FeaturePart = "lines" | "fills" | "outlines";

/** The deepest level a feature set's parts are drawn at. */
const FEATURE_DRAWN_DEEPEST = 22;
/** The value a part's raster holds where it covers a pixel. */
const COVERED = 1;
/** Lines and outlines are never drawn thinner than this, in pixels of the tile they're drawn in. */
const THINNEST_PIXELS = 1.5;
/** A pixel counts as covered once a stroke or fill covers this share of it. */
const COVERED_SHARE = 0.4;

/** The renderer's raster name for a part of a feature set, which never clashes with a raster's. */
const featureRasterName = (featureSet: string, part: FeaturePart) =>
  `features:${featureSet}:${part}`;

/** The renderer's raster a paint step covers: a raster by its name, or a feature set's part. */
export function coveredRaster(
  covers: BasemapPaintStep["covers"],
  rasters: Readonly<Record<string, unknown>>,
): string {
  if (typeof covers !== "string") return featureRasterName(covers.featureSet, covers.part);
  return covers in rasters ? covers : featureRasterName(covers, "lines");
}

/** The parts of each feature set a renderer draws, by raster name. */
export function featureParts(
  featureSets: Readonly<Record<string, Pick<BasemapFeatureSetRules, "geometry">>>,
): { name: string; featureSet: string; part: FeaturePart }[] {
  return Object.entries(featureSets).flatMap(([featureSet, { geometry }]) =>
    (geometry === "line" ? (["lines"] as const) : (["fills", "outlines"] as const)).map(part => ({
      name: featureRasterName(featureSet, part),
      featureSet,
      part,
    })),
  );
}

/** A part's raster as the renderer's world lists it. */
export const featureRendererRaster = (name: string) => ({
  name,
  type: "category" as const,
  deepest: FEATURE_DRAWN_DEEPEST,
  step: 1,
  enlarge: "nearest" as const,
  feature: true,
});

/** The paint steps a feature set brought into a draft is drawn with, until an author changes them. */
export function defaultFeatureSteps(
  featureSet: string,
  geometry: BasemapFeatureSetRules["geometry"],
): BasemapPaintStep[] {
  const ink = ["theme", "#2f5f8a", "#9cc3e6"];
  return geometry === "line"
    ? [{ covers: featureSet, appearance: { color: ink }, blend: "over" }]
    : [
        {
          covers: { featureSet, part: "fills" },
          appearance: { color: ink, opacity: 0.25 },
          blend: "over",
        },
        { covers: { featureSet, part: "outlines" }, appearance: { color: ink }, blend: "over" },
      ];
}

/** The detail a part reaches, which the streamer loads to: every place, as deep as it's drawn. */
const drawnReach: Pick<LeafTree, "leafAt"> = {
  leafAt: (face, s, t) => placeContaining(face, FEATURE_DRAWN_DEEPEST, s, t),
};

/** One stored tile of a feature set, and the place it's at. */
export interface StoredFeatureTile {
  place: TilePlace;
  tile: BasemapFeatureTile;
}

/**
 * A feature tile from its bytes: gzipped, as its file stores them, or plain JSON, as the tiles
 * Worker serves them.
 */
export async function readFeatureTileBytes(bytes: Uint8Array): Promise<BasemapFeatureTile> {
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) return decodeFeatureTile(bytes);
  return BasemapFeatureTile.parse(JSON.parse(new TextDecoder().decode(bytes)));
}

/**
 * Reads a feature set's tiles for drawing: the stored tile at a place where its detail goes that
 * deep, else the finest one above it, which holds every piece at the place exactly.
 */
export function storedFeatureTiles(
  tree: LeafTree,
  read: (place: TilePlace) => Promise<BasemapFeatureTile | null>,
): (place: TilePlace) => Promise<StoredFeatureTile | null> {
  const held = new Map<string, Promise<BasemapFeatureTile | null>>();
  const readHeld = (place: TilePlace) => {
    const key = placeKey(place);
    let tile = held.get(key);
    if (!tile) {
      tile = read(place).catch(() => null);
      held.set(key, tile);
      // The most recently read tiles stay, so a view zooming in draws from what it has.
      if (held.size > 64) held.delete(held.keys().next().value as string);
    }
    return tile;
  };
  return async place => {
    const across = 2 ** (place.level - BASEMAP_COARSEST_LEVEL);
    const leaf = tree.leafAt(place.face, (place.x + 0.5) / across, 1 - (place.y + 0.5) / across);
    const stored = leaf.level >= place.level ? place : leaf;
    const tile = await readHeld(stored);
    return tile && { place: stored, tile };
  };
}

/**
 * One part of a feature tile drawn into a place at or under the tile's: a category tile holding
 * `COVERED` wherever the part covers a pixel. A place no tile holds is drawn empty, so the renderer
 * never shows a coarser tile's wider strokes there instead.
 */
function drawFeaturePart(
  stored: StoredFeatureTile | null,
  place: TilePlace,
  part: FeaturePart,
  geometry: BasemapGeometry,
): Uint8Array {
  const pixels = new Uint8Array(TILE_AREA).fill(NO_CATEGORY);
  const canvas = new OffscreenCanvas(BASEMAP_TILE_PIXELS, BASEMAP_TILE_PIXELS);
  const context = canvas.getContext("2d");
  if (!stored || !context) return pixels;
  const across = 2 ** (place.level - BASEMAP_COARSEST_LEVEL);
  const scale = (BASEMAP_TILE_PIXELS * across) / FACE_UNITS;
  const pointAt = (positions: number[], vertex: number): [number, number] => [
    (positions[vertex * 2] ?? 0) * scale - place.x * BASEMAP_TILE_PIXELS,
    (positions[vertex * 2 + 1] ?? 0) * scale - place.y * BASEMAP_TILE_PIXELS,
  ];
  const faceMeters =
    geometry.kind === "sphere" ? (Math.PI / 2) * geometry.radiusMeters : geometry.widthMeters;
  const metersPerPixel = faceMeters / (BASEMAP_TILE_PIXELS * across);
  context.fillStyle = "#fff";
  context.strokeStyle = "#fff";
  context.lineCap = "round";
  context.lineJoin = "round";

  for (const feature of stored.tile.features) {
    if (part === "fills") {
      context.beginPath();
      for (const piece of feature.pieces) {
        const count = piece.positions.length / 2;
        for (let vertex = 0; vertex < count; vertex++) {
          const [x, y] = pointAt(piece.positions, vertex);
          if (vertex === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        }
        context.closePath();
      }
      context.fill("evenodd");
      continue;
    }
    for (const piece of feature.pieces) {
      const count = piece.positions.length / 2;
      if (part === "outlines") {
        // A ring's edges along the tile's clipped side aren't the polygon's, so they're skipped.
        const clipped = new Set(piece.clippedEdges ?? []);
        context.lineWidth = THINNEST_PIXELS;
        context.beginPath();
        for (let vertex = 0; vertex < count; vertex++) {
          if (clipped.has(vertex)) continue;
          context.moveTo(...pointAt(piece.positions, vertex));
          context.lineTo(...pointAt(piece.positions, (vertex + 1) % count));
        }
        context.stroke();
        continue;
      }
      // A line's width can change along it, so each edge is stroked at its ends' mean width.
      for (let vertex = 0; vertex + 1 < count; vertex++) {
        const meters = ((piece.widths?.[vertex] ?? 0) + (piece.widths?.[vertex + 1] ?? 0)) / 2;
        context.lineWidth = Math.max(THINNEST_PIXELS, meters / metersPerPixel);
        context.beginPath();
        context.moveTo(...pointAt(piece.positions, vertex));
        context.lineTo(...pointAt(piece.positions, vertex + 1));
        context.stroke();
      }
    }
  }

  const { data } = context.getImageData(0, 0, BASEMAP_TILE_PIXELS, BASEMAP_TILE_PIXELS);
  for (let index = 0; index < TILE_AREA; index++) {
    if ((data[index * 4 + 3] ?? 0) >= COVERED_SHARE * 255) pixels[index] = COVERED;
  }
  return pixels;
}

/** A part of a feature set as the streamer loads it, drawn from the set's stored tiles. */
export function streamedFeaturePart(
  { name, part }: { name: string; part: FeaturePart },
  geometry: BasemapGeometry,
  readStored: (place: TilePlace) => Promise<StoredFeatureTile | null>,
) {
  return {
    name,
    type: "category" as const,
    tree: drawnReach,
    read: async (place: TilePlace) =>
      drawFeaturePart(await readStored(place), place, part, geometry),
  };
}
